<template>
  <VFilters
    v-if="!loading"
    :allowed-filters="allowedFilters"
    :filter-groups="filterGroups"
    :default-filters="$route.query.filter || {}"
  />
</template>

<script>
import { orderTagsClient, membersClient } from '@/api/clients';
import VFilters from '@/components/Common/VFilters.vue';

export default {
  name: 'SaleOrderFilters',
  components: {
    VFilters,
  },
  data() {
    return {
      loading: false,
      tagOptions: [],
      assigneeOptions: [],
      allowedFilters: {},
      filterGroups: {
        status: {
          label: 'status',
          name: 'status',
          filters: ['has_status', 'doesnt_have_status', 'status_in', 'status_not_in'],
          value: null,
        },
        orderStatus: {
          label: 'order status',
          name: 'order_status',
          filters: [
            'has_order_status',
            'doesnt_have_order_status',
            'order_status_in',
            'order_status_not_in',
          ],
          value: null,
        },
        tag: {
          label: 'tag',
          name: 'tag',
          filters: ['has_tags', 'doesnt_have_tags', 'tags_in', 'tags_not_in'],
          value: null,
        },
        assignee: {
          label: 'assignee',
          name: 'assignee',
          filters: ['has_assignee', 'doesnt_have_assignee', 'assignee_in', 'assignee_not_in'],
          value: null,
        },
      },
    };
  },

  async created() {
    try {
      this.loading = true;

      await Promise.all([this.loadOrderTagOptions(), this.loadAssignedUserOptions()]);

      const statusOptions = [
        {
          label: 'Draft',
          value: 1,
        },
        {
          label: 'Production',
          value: 2,
        },
        {
          label: 'Client Feedback',
          value: 3,
        },
        {
          label: 'To upload',
          value: 4,
        },
        {
          label: 'Pending Channel approval',
          value: 5,
        },
        {
          label: 'Running',
          value: 6,
        },
        {
          label: 'Client Satisfaction',
          value: 7,
        },
        {
          label: 'Completed',
          value: 8,
        },
        {
          label: 'Ελλιπή στοιχεία',
          value: 9,
        },
      ];

      const orderStatusOptions = [
        {
          label: 'Draft',
          value: 'draft',
        },
        {
          label: 'Pending',
          value: 'pending',
        },
        {
          label: 'In progress',
          value: 'in progress',
        },
        {
          label: 'In review',
          value: 'in review',
        },
        {
          label: 'Completed',
          value: 'completed',
        },
      ];

      this.allowedFilters = {
        has_status: {
          label: 'with',
          multiple: false,
          name: 'has_status',
          type: Number,
          value: 1,
        },
        doesnt_have_status: {
          label: 'without',
          multiple: false,
          name: 'has_status',
          type: Number,
          value: 0,
        },
        status_in: {
          label: 'is one of ...',
          multiple: true,
          name: 'status_in',
          options: statusOptions,
          type: String,
          value: [],
        },
        status_not_in: {
          label: 'is not one of ...',
          multiple: true,
          name: 'status_not_in',
          options: statusOptions,
          type: String,
          value: [],
        },
        has_order_status: {
          label: 'with',
          multiple: false,
          name: 'has_order_status',
          type: Number,
          value: 1,
        },
        doesnt_have_order_status: {
          label: 'without',
          multiple: false,
          name: 'has_order_status',
          type: Number,
          value: 0,
        },
        order_status_in: {
          label: 'is one of ...',
          multiple: true,
          name: 'order_status_in',
          options: orderStatusOptions,
          type: String,
          value: [],
        },
        order_status_not_in: {
          label: 'is not one of ...',
          multiple: true,
          name: 'order_status_not_in',
          options: orderStatusOptions,
          type: String,
          value: [],
        },
        has_tags: {
          label: 'with',
          multiple: false,
          name: 'has_tags',
          type: Number,
          value: 1,
        },
        doesnt_have_tags: {
          label: 'without',
          multiple: false,
          name: 'has_tags',
          type: Number,
          value: 0,
        },
        tags_in: {
          label: 'is one of ...',
          multiple: true,
          name: 'tags_in',
          options: this.tagOptions,
          type: Number,
          value: [],
        },
        tags_not_in: {
          label: 'is not one of ...',
          multiple: true,
          name: 'tags_not_in',
          options: this.tagOptions,
          type: Number,
          value: [],
        },
        has_assignee: {
          label: 'with',
          multiple: false,
          name: 'has_assignee',
          type: Number,
          value: 1,
        },
        doesnt_have_assignee: {
          label: 'without',
          multiple: false,
          name: 'has_assignee',
          type: Number,
          value: 0,
        },
        assignee_in: {
          label: 'is one of ...',
          multiple: true,
          name: 'assignee_in',
          options: this.assigneeOptions,
          type: String,
          value: [],
        },
        assignee_not_in: {
          label: 'is not one of ...',
          multiple: true,
          name: 'assignee_not_in',
          options: this.assigneeOptions,
          type: String,
          value: [],
        },
      };
    } catch (error) {
      //
    } finally {
      this.loading = false;
    }
  },

  methods: {
    async loadOrderTagOptions() {
      try {
        const result = await orderTagsClient.list({
          params: {
            filter: {
              owns_project: 1,
            },
          },
        });

        this.setOrderTagOptions(result.data.data);
      } catch (error) {
        //
      }
    },
    setOrderTagOptions(tags) {
      this.tagOptions = tags.map((tag) => ({
        label: tag.name,
        value: tag.id,
      }));
    },
    async loadAssignedUserOptions() {
      try {
        const result = await membersClient.list({
          params: {
            filter: {
              has_order_assigned: 1,
            },
          },
        });

        this.setAssignedUserOptions(result.data.data);
      } catch (error) {
        //
      }
    },
    setAssignedUserOptions(users) {
      this.assigneeOptions = users.map((user) => ({
        label: user.fullName,
        value: user.id,
      }));
    },
  },
};
</script>
